<template>
  <!-- Base variant -->
  <div
    v-if="variant === 'compact'"
    :class="cx('inline-flex w-fit items-center gap-1.5 rounded-[4px] border border-[--color-neutral-neutral-lighter] bg-[--color-neutral-white] p-2', $attrs.class)"
  >
    <Icon
      name="clock"
      class="size-3.5 shrink-0 text-[--color-neutral-neutral]"
    />
    <span class="text-xs text-[--color-neutral-neutral]">
      <span class="font-semibold">{{ remainingQuota }}/{{ totalQuota }}</span>
      <span class="font-normal"> left today</span>
    </span>
  </div>

  <!-- Free variant -->
  <div v-else :class="cx('w-full max-w-[241px] py-1', $attrs.class)">
    <div class="flex items-start justify-between gap-3">
      <div class="flex min-w-0 gap-2">
        <div class="flex gap-1">
          <Icon
            name="clock"
            class="size-3.5 shrink-0 text-[--color-neutral-neutral]"
          />
          <span class="truncate text-xs font-normal leading-tight text-[--color-neutral-neutral]">
            Daily searches
          </span>
        </div>

        <span class="shrink-0 text-xs font-semibold leading-tight text-[--color-neutral-black]">
          <span :class="remainingTextClass">{{ remainingQuota }}</span>/{{ totalQuota }} left
        </span>
      </div>

      <button
        type="button"
        class="flex shrink-0 items-center justify-center gap-[5px] text-xs font-medium leading-tight text-[#b8651b]"
        @click="handleUpgradeClick"
      >
        <span>Upgrade</span>
        <Icon name="arrow-up" class="size-3.5" />
      </button>
    </div>

    <div class="mt-3 flex gap-1">
      <span
        v-for="segment in progressSegments"
        :key="segment"
        :class="segmentClass(segment)"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useRouter } from 'vue-router';

import Icon from '@/common/components/atoms/Icon';
import { mixPanelEvents } from '@/common/constants';
import type { Quota } from '@/common/types/shop';
import { track } from '@/helpers/analytics/mixpanel';
import { cx } from '@/helpers/cva';

type Variant = 'upgrade' | 'compact';

interface Props {
  quota: Quota;
  variant?: Variant;
  source?: string;
}

const props = withDefaults(defineProps<Props>(), {
  variant: 'upgrade',
  source: 'Free Shop Info',
});

const router = useRouter();

const totalQuota = computed(() => Math.max(props.quota.feature_quota ?? 0, 0));
const consumedQuota = computed(() => Math.min(Math.max(props.quota.current_usage ?? 0, 0), totalQuota.value));
const remainingQuota = computed(() => Math.max(totalQuota.value - consumedQuota.value, 0));
const isWarningState = computed(() => consumedQuota.value >= 4);
const progressSegments = computed(() => Array.from({ length: totalQuota.value }, (_, index) => index + 1));

const remainingTextClass = computed(() => (
  isWarningState.value ? 'text-[#b80d00]' : 'text-[--color-brand-forrest-green-80]'
));

function segmentClass(segment: number) {
  const baseClass = 'block h-[3px] w-[45px] rounded-full';

  if (segment <= consumedQuota.value) {
    return `${baseClass} ${isWarningState.value ? 'bg-[--color-brand-tangerine]' : 'bg-[--color-brand-forrest-green-80]'}`;
  }

  return `${baseClass} bg-[--color-neutral-neutral-very-light]`;
}

function handleUpgradeClick() {
  track(mixPanelEvents.CLICKED_UPGRADE, {
    Source: props.source,
  });

  router.push('/plans');
}
</script>
